import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Spinner } from "react-bootstrap";
import { useNotification } from "../../../hooks/useNotification";
import { FormLabel } from "../../../components/atoms/label";
import { FormOption, FormSelect } from "../../../components/atoms/select/select";
import { ModalBodyT1 } from "../../../components/organisms/Modal/Body";
import { ModalOverlay, ModalT1 } from "../../../components/organisms/Modal";
import { PortalFooter } from "../../../components/molecules/Footer";
import { PortalHeader } from "../../../components/molecules/Header";
import { CLOSE_REASON_OPTIONS } from "./closeReasons";
import {
  AcknowledgeBox,
  BlockerBanner,
  CloseModalBody,
  FieldBlock,
  IntroText,
  LoadingRow,
  MessageLine,
  MessageList,
  OrderList,
  OrderName,
  OrderRow,
  OrderRowHeader,
  StatusPill,
} from "./closeDocument.styles";

/**
 * Shared close dialog for purchase orders and shipments (preview, reason).
 *
 * @param {{
 *   closeModal: function(): void,
 *   orders?: !Array<{id: number, name?: string}>,
 *   setSelectedOrder?: function(!Array): void,
 *   title: string,
 *   submitLabel: string,
 *   nounSingular: string,
 *   nounPlural: string,
 *   idKey: string,
 *   usePreviewMutation: function,
 *   useCloseMutation: function,
 *   previewErrorMessage?: string,
 *   closeErrorMessage?: string,
 * }} props
 */
export const CloseDocumentModal = ({
  closeModal,
  orders = [],
  setSelectedOrder,
  title,
  submitLabel,
  nounSingular,
  nounPlural,
  idKey,
  usePreviewMutation,
  useCloseMutation,
  previewErrorMessage = "Could not load close preview.",
  closeErrorMessage = "Failed to close.",
}) => {
  const notify = useNotification();
  const [preview, { isLoading: isPreviewLoading }] = usePreviewMutation();
  const [closeDocuments, { isLoading: isClosing }] = useCloseMutation();
  const [results, setResults] = useState([]);
  const [reason, setReason] = useState(CLOSE_REASON_OPTIONS[0].value);
  const [acknowledgeWarnings, setAcknowledgeWarnings] = useState(false);

  const orderIds = useMemo(() => orders.map(item => item.id), [orders]);
  const count = orders.length;
  const noun = count === 1 ? nounSingular : nounPlural;

  useEffect(() => {
    if (!orderIds.length) return;
    preview({ ids: orderIds })
      .unwrap()
      .then(response => setResults(response.results || []))
      .catch(error => {
        notify({
          type: "error",
          message: error?.data?.detail || previewErrorMessage,
        });
      });
  }, [notify, orderIds, preview, previewErrorMessage]);

  const hasBlockers = results.some(row => !row.can_close);
  const hasWarnings = results.some(row => row.warnings?.length > 0);
  const canSubmit =
    !hasBlockers && results.length > 0 && (!hasWarnings || acknowledgeWarnings) && reason;

  const handleOverlayClick = event => {
    if (event.target === event.currentTarget) {
      closeModal();
    }
  };

  const stopPropagation = event => {
    event.stopPropagation();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      const response = await closeDocuments({
        ids: orderIds,
        reason,
        acknowledge_warnings: acknowledgeWarnings,
      }).unwrap();
      notify({ type: "success", message: response.detail });
      setSelectedOrder?.([]);
      closeModal();
    } catch (error) {
      notify({
        type: "error",
        message: error?.data?.detail || closeErrorMessage,
      });
    }
  };

  const getRowTone = row => {
    if (!row.can_close) return "blocked";
    if (row.warnings?.length) return "warning";
    return "ready";
  };

  const getRowStatusLabel = row => {
    if (!row.can_close) return "Can't close";
    if (row.warnings?.length) return "Has warnings";
    return "Ready to close";
  };

  const modalContent = (
    <ModalOverlay onClickCallback={handleOverlayClick}>
      <ModalT1 $minWidth={520} $maxWidth={640} onClick={stopPropagation}>
        <PortalHeader title={title} onClose={closeModal} />
        <ModalBodyT1>
          <CloseModalBody>
            <IntroText>
              You are closing {count} {noun}. Review each item below, choose a reason, and confirm.
            </IntroText>

            {isPreviewLoading && (
              <LoadingRow>
                <Spinner animation="border" size="sm" variant="secondary" />
                Checking {noun}…
              </LoadingRow>
            )}

            {!isPreviewLoading && results.length > 0 && (
              <OrderList>
                {results.map(row => {
                  const tone = getRowTone(row);
                  return (
                    <OrderRow key={row[idKey]}>
                      <OrderRowHeader>
                        <OrderName>{row.name}</OrderName>
                        <StatusPill $tone={tone}>{getRowStatusLabel(row)}</StatusPill>
                      </OrderRowHeader>
                      {(row.blockers?.length > 0 || row.warnings?.length > 0) && (
                        <MessageList>
                          {row.blockers?.map(text => (
                            <MessageLine key={`b-${text}`} $variant="blocker">
                              {text}
                            </MessageLine>
                          ))}
                          {row.warnings?.map(text => (
                            <MessageLine key={`w-${text}`} $variant="warning">
                              {text}
                            </MessageLine>
                          ))}
                        </MessageList>
                      )}
                    </OrderRow>
                  );
                })}
              </OrderList>
            )}

            {hasBlockers && (
              <BlockerBanner>
                One or more {nounPlural} cannot be closed until the blockers above are resolved.
              </BlockerBanner>
            )}

            {hasWarnings && !hasBlockers && (
              <AcknowledgeBox>
                <input
                  type="checkbox"
                  checked={acknowledgeWarnings}
                  onChange={event => setAcknowledgeWarnings(event.target.checked)}
                />
                I understand the warnings above
              </AcknowledgeBox>
            )}

            <FieldBlock>
              <FormLabel htmlFor="close-document-reason">Reason</FormLabel>
              <FormSelect
                id="close-document-reason"
                value={reason}
                onChange={event => setReason(event.target.value)}
              >
                {CLOSE_REASON_OPTIONS.map(option => (
                  <FormOption key={option.value} value={option.value}>
                    {option.label}
                  </FormOption>
                ))}
              </FormSelect>
            </FieldBlock>
          </CloseModalBody>
        </ModalBodyT1>
        <PortalFooter
          className="for-confirm-modal"
          applyTitle={submitLabel}
          cancelTitle="Cancel"
          onApply={handleSubmit}
          onCancel={closeModal}
          isDisabled={!canSubmit || isClosing || isPreviewLoading}
          isLoading={isClosing}
          applyVariant="primary"
        />
      </ModalT1>
    </ModalOverlay>
  );

  const portalRoot = document.getElementById("root-modals") || document.body;
  return createPortal(modalContent, portalRoot);
};
